<script setup lang="ts">
import type { PropType } from "vue";
import type { Section } from "~/types";
import type { Swiper as SwiperInstance } from "swiper";

defineProps({
  title: {
    type: String as PropType<Section["title"]>,
    required: true,
  },
  designType: {
    type: String as PropType<Section["design_type"]>,
    required: true,
  },
  description: {
    type: String as PropType<Section["description"]>,
    default: "",
  },
  tiles: {
    type: Array as PropType<Section["tiles"]>,
    default: () => [],
  },
});

const sixTilesDesign = {
  300: {
    slidesPerView: 2,
  },
  1024: {
    slidesPerView: 6,
  },
};

const threeTilesDesign = {
  300: {
    slidesPerView: 1,
  },
  1024: {
    slidesPerView: 3,
  },
};

const swiperInstance = ref<SwiperInstance | null>(null);
const isBeginning = ref(true);
const isEnd = ref(false);

function onSwiperCreated(swiper: SwiperInstance) {
  swiperInstance.value = swiper;
  isBeginning.value = swiper.isBeginning;
  isEnd.value = swiper.isEnd;

  swiper.on("slideChange", () => {
    isBeginning.value = swiper.isBeginning;
    isEnd.value = swiper.isEnd;
  });
}

function nextSlide() {
  if (swiperInstance.value) {
    swiperInstance.value.slideNext();
  }
}

function prevSlide() {
  if (swiperInstance.value) {
    swiperInstance.value.slidePrev();
  }
}
</script>

<template>
  <ClientOnly>
    <div>
      <div class="mb-5 flex items-center justify-between text-sm">
        <div>
          <h3 class="mb-2 text-3xl font-bold">
            {{ title }}
          </h3>
          <div class="text-surface-300">{{ description }}</div>
        </div>
        <div class="flex gap-2">
          <div
            class="back-swiper-btn flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-surface-border bg-surface-bg"
            :class="[{ '!cursor-not-allowed opacity-20': isBeginning }]"
            @click="prevSlide"
          >
            <font-awesome-icon :icon="['fas', 'arrow-left']" />
          </div>
          <div
            class="next-swiper-btn flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-surface-border bg-surface-bg"
            :class="[{ '!cursor-not-allowed opacity-20': isEnd }]"
            @click="nextSlide"
          >
            <font-awesome-icon :icon="['fas', 'arrow-right']" />
          </div>
        </div>
      </div>

      <div class="relative">
        <Swiper
          :breakpoints="
            designType === 'design_6_tile' ? sixTilesDesign : threeTilesDesign
          "
          @swiper="onSwiperCreated"
        >
          <SwiperSlide v-for="(tile, index) in tiles" :key="`item_${index}`">
            <img class="px-1" :src="tile.image" alt="tile-img" />
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </ClientOnly>
</template>
